<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
const {
    Link: PocketLink,
    Icon,
    Pill,
    CheckboxOption,
} = UIKit

type TCheckboxOption = {
    id: string
    label: string
    enabled: boolean
}

export type TDropdownCheckboxMenu = {
    filterType: string
    items: TCheckboxOption[]
    filterMenuLabel: string
    expanded?: boolean
    allowCollapse?: boolean
    emptyStateMessage?: string
}

const props = withDefaults(defineProps<{
    dropdownMenus: TDropdownCheckboxMenu[]
    filterLabel?: string
    filterNameMap?: { [filterType: string]: string }
}>(), {
    filterLabel: 'Filter',
    filterNameMap: undefined,
})

const emit = defineEmits<{
    'updateFilters': [{ filterType: string; item: TCheckboxOption }]
    'dropdownClosed': []
}>()

const dropdowns = ref(props.dropdownMenus)
const showFilterDropdown = ref(false)
const scrollContainer = useTemplateRef('scrollContainer')

const activeFilters = computed(() => {
    return dropdowns.value
        .flatMap(filterMenu => filterMenu.items.map(item => ({ ...item, filterType: filterMenu.filterType }))
            .filter(item => item.enabled))
})

const filterIdToIndexMap = computed(() => {
    return dropdowns.value.reduce((acc, menu, menuIndex) => {
        const allMenuItems = menu.items.flat()
        allMenuItems.forEach((item, itemIndex) => {
            acc[item.id] = { menuIndex, itemIndex }
        })
        return acc
    }, {} as { [key: string]: { menuIndex: number; itemIndex: number } })
})

const expandOrCollapseMenu = (allowCollapse: boolean | undefined, menuIndex: number, event: MouseEvent) => {
    if (allowCollapse && dropdowns.value[menuIndex]) {
        dropdowns.value[menuIndex].expanded = !dropdowns.value[menuIndex].expanded
    }
    const target = event.target as HTMLElement
    const container = scrollContainer.value as HTMLElement

    if (container && target) {
        const containerTop = container.getBoundingClientRect().top
        const targetTop = target.getBoundingClientRect().top
        const offset = targetTop - containerTop

        nextTick(() => {
            container.scrollTop += offset
        })
    }
}

const selectItem = (filterType: string, item: TCheckboxOption) => {
    emit('updateFilters', { filterType, item })
}

const toggleDropdown = (isClosing?: boolean) => {
    if (isClosing) {
        showFilterDropdown.value = false
    } else {
        showFilterDropdown.value = !showFilterDropdown.value
    }
    if (!showFilterDropdown.value) {
        emit('dropdownClosed')
    }
}

const getFilterName = (filter: typeof activeFilters.value[0]) => {
    return props.filterNameMap?.[filter.filterType]
        ? `${props.filterNameMap[filter.filterType]}: ${filter.label}`
        : filter.label
}

const filterTagClicked = (filter: TCheckboxOption) => {
    const clickedFilter = filterIdToIndexMap.value[filter.id]
    if (!clickedFilter) {
        return
    }
    const { menuIndex, itemIndex } = clickedFilter
    const filterType = dropdowns.value[menuIndex]?.filterType
    const item = dropdowns.value[menuIndex]?.items[itemIndex]
    if (item) {
        item.enabled = false
    }
    if (filterType && item) {
        emit('updateFilters', { filterType, item })
    }
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
        e.stopPropagation()
        toggleDropdown(true)
    }
}

const clickListener = (e: MouseEvent) => {
    if (showFilterDropdown.value) {
        const targetEl = e.target as HTMLElement
        if (!targetEl?.closest('.multi-filter') || targetEl.closest('.multi-filter__active-filters-container')
            && showFilterDropdown.value && document.body.contains(targetEl)
        ) {
            toggleDropdown(true)
        }
    }
}

onMounted(() => {
    document.addEventListener('click', clickListener)
    document.addEventListener('keydown', keydownListener)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', clickListener)
    document.removeEventListener('keydown', keydownListener)
})
</script>

<template>
    <div class="multi-filter" v-if="dropdownMenus.length">
        <div class="multi-filter__dropdown-btn">
            <PocketLink
                :has-icon="true"
                :aria-expanded="showFilterDropdown ? 'true' : 'false'"
                class="multi-filter__link"
                :class="{ 'multi-filter__link--open': showFilterDropdown }"
                role="button"
                type="tertiary"
                tabindex="0"
                @keypress.enter="toggleDropdown()"
                @click="toggleDropdown()"
            >
                <Icon :type="activeFilters.length ? 'filterActive' : 'filter'" />
                <span class="multi-filter__text">
                    {{ filterLabel }}
                </span>
            </PocketLink>
            <div
                v-show="showFilterDropdown"
                class="multi-filter__dropdown"
                ref="scrollContainer">
                <div v-for="(menu, menuIndex) in dropdowns" :key="menu.filterType">
                    <div
                        class="multi-filter__menu-title"
                        :class="{
                            'multi-filter__menu-title--collapsed': !menu.expanded,
                            'multi-filter__menu-title--collapsible': menu.allowCollapse
                        }"
                        @click="expandOrCollapseMenu(menu.allowCollapse, menuIndex, $event)"
                    >
                        {{ menu.filterMenuLabel }}
                        <Icon
                            v-if="menu.allowCollapse"
                            type="accordionArrow"
                            class="multi-filter__accordion-arrow"
                            :class="{ 'multi-filter__accordion-arrow--asc': menu.expanded }"
                        />
                    </div>
                    <div
                        v-show="menu.expanded"
                        class="multi-filter__menus-container"
                    >
                        <div v-if="!menu.items.length" class="multi-filter__empty-state">
                            {{ menu.emptyStateMessage || 'No filters are available for this category' }}
                        </div>
                        <CheckboxOption
                            v-for="item in menu.items"
                            :key="item.id"
                            v-model="item.enabled"
                            :label="item.label"
                            class="multi-filter__checkbox-menu"
                            @click="selectItem(menu.filterType, item)"
                        >
                        </CheckboxOption>
                    </div>
                </div>
            </div>
        </div>
        <div class="multi-filter__active-filters-container">
            <div class="multi-filter__active-filters-text">
                <span>Filters:</span>
                <span v-if="!activeFilters.length"> none applied</span>
            </div>
            <Pill
                v-for="filter in activeFilters"
                :key="filter.id"
                :show-close="true"
                @click="filterTagClicked(filter)"
            >
                {{ getFilterName(filter) }}
            </Pill>
        </div>
    </div>
</template>

<style lang="scss" scoped>

.multi-filter {
    position: relative;
    z-index: 2;
    width: 100%;
    display: flex;

    &__dropdown {
        position: absolute;
        left: 0;
        top: 28px;
        width: 240px;
        border-radius: 8px;
        z-index: 1;
        box-sizing: border-box;
        background: $white;
        box-shadow: 0 6px 22px 0 rgba($brand-black, 0.2);
        max-height: 305px;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 20px;
        scroll-behavior: smooth;
    }

    &__dropdown-btn {
        min-width: 155px;
    }

    &__link {
        align-items: center;
        display: flex;
        flex-shrink: 0;

        &--open {
            color: $brand-black !important;
        }

        svg {
            margin-right: 3px;
        }
    }

    &__text {
        margin-top: 1px;
        line-height: 20px;
    }

    &__menu-title {
        display: flex;
        color: $slate;
        font-size: 12px;
        letter-spacing: 1px;
        line-height: 16px;
        text-align: left;
        padding: 13px 12px 13px 16px;
        text-transform: uppercase;
        align-items: center;
        cursor: default;

        &--collapsible {
            cursor: pointer;
        }

        &--collapsed {
            padding-bottom: 0;
        }
    }

    &__accordion-arrow {
        width: 10px;
        height: 10px;
        transition: 0.3s transform;
        color: $brand-blue;
        margin-left: 4px;
        &--asc {
            transform: rotate(180deg);
        }
    }

    &__checkbox-menu {
        padding-left: 18px;
        padding-right: 18px;
    }

    &__active-filters-container {
        min-height: 26px;
        margin-left: 16px;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    &__active-filters-text {
        margin-right: 8px;
        line-height: 18px;
        font-size: 13px;
        color: $slate;
    }

    &__filter-chips-label {
        color: $pickled-bluewood;
        font-weight: 600;
        font-size: 13px;
        line-height: 18px;
        margin-bottom: 11px;
    }

    &__filter-chips {
        display: flex;
        flex-wrap: wrap;
        max-width: 522px;
    }

    &__empty-state {
        padding: 0 40px;
        color: $ash;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-top: 19px;
    }

    :deep(.uikit-pill) {
        top: 2px;
    }
}
</style>
